/**
 * Shared primitives for the dashboard detail panels (DealDetailPanel +
 * LeadDetailPanel). Extracted verbatim from the two panels, which previously
 * duplicated them — keep class strings and snap heights identical so both
 * panels render exactly the same chrome.
 */

import { ExternalLink } from 'lucide-react';
import { clsx } from 'clsx';

/** Bottom-sheet snap points shared by every mobile panel. */
export type SnapPoint = 'peek' | 'half' | 'full';
export const SNAP_HEIGHTS: Record<SnapPoint, string> = {
  peek: '20vh',
  half: '55vh',
  full: '92vh',
};

/** Tiny uppercase section heading used between panel content groups. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500">{children}</div>
  );
}

/** Icon + label contact row, optionally wrapped in a (possibly external) link. */
export function Row({
  icon,
  label,
  href,
  external,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  external?: boolean;
  className?: string;
}) {
  const content = (
    <div className={clsx('flex items-center gap-2 text-sm text-neutral-200', className)}>
      <span className="text-neutral-500">{icon}</span>
      <span className="truncate">{label}</span>
      {external && href && <ExternalLink className="h-3 w-3 text-neutral-500" />}
    </div>
  );
  if (href) {
    return (
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className="-mx-1 block rounded px-1 py-0.5 hover:bg-white/[0.04]">
        {content}
      </a>
    );
  }
  return content;
}
